import TempStorageService from "@assets/js/imaios-connection-management/temp-storage.service";
import {STORAGE_KEY, PRODUCT} from "@assets/js/imaios-connection-management/config.constant";
import NOTIFICATIONS from "@assets/js/notifications.constant";
import { Subscriber } from "@qphi/publisher-subscriber";
import { get, getCookie, fetch } from "../imaios-global/imaios-global";
import { COOKIE, THIRD_PARTY_PROVIDER_PRODUCT } from './config.constant';

/**
 * @typedef {Object} UserProductAccess
 * @see PHP ProductAccessService::getProductAccess
 */

/**
 * @typedef {Object} LocalUserData The shape of the data preserved in local storage for a user, might not be accurate or up to date.
 *
 * @property id {string | null} user id
 * @property identifier {string | null} "AUTHENTICATION_PHONE" or "AUTHENTICATION_EMAIL" (algo backend side on UserCustomer class)
 * @property value {string | null} user's email or phone (depend of identifier)
 * @property firstName {string | null}
 * @property lastName {string | null}
 * @property is_login_as {boolean}
 * @property b2b_customer_name {string | null}
 * @property isB2BAdministrator {boolean}
 * @property isIp {boolean}
 * @property access {UserProductAccess}
 * @property session_id {string | null}
 */
class LoginManagementService {
    constructor() {
        this.isInitialized = false;
        this.subscriber = new Subscriber('login-management-subscriber');
        this.onConnectionCheckFinished = async () => {};
        this.awaitableConnection = new Promise(resolve => this.onConnectionCheckFinished = resolve);

        window.addEventListener('DOMContentLoaded', event => {
            this.subscribeToEvents();
        });

        window.addEventListener('pageshow', event => {
            if (event.persisted) {
                imaios.store('are-credentials-reliable', false);
                this.loadLogin();
            }

            if ((
                event.persisted ||
                (
                    typeof window.performance != "undefined" &&
                    window.performance.getEntriesByType('navigation').some(entries => entries.type === 'back_forward')
                )
            ) && get('guard:user-connected')) {
                if (!this.isConnected()) {
                    window.location.reload(); // Force reload to refresh WS
                }
            }

            this.subscribeToEvents();
        });

        window.addEventListener('pagehide', () => {
            imaios.store('are-credentials-reliable', false);
            this.unsubscribe();
        });

        this.subscribeToEvents();
    }

    isConnected() {
       return this.getValueFromStorage() !== null;
    }

    getUserInformation() {
        return new Promise((resolve, reject) => {
            const qs = 'vw=' + document.documentElement.clientWidth + '&vh=' + document.documentElement.clientHeight + '&dw=' + window.screen.width + '&dh=' + window.screen.height;
            fetch(`/${imaios.getSiteAccess()}/api/user/information?${qs}`)
                .then(res => res.json())
                .then(data => resolve(data))
                .catch(() => reject())
                .finally(() => this.onConnectionCheckFinished());
        });
    }

    loadLogin() {
        this.checkExpire();
        if (TempStorageService.get(STORAGE_KEY.IMAIOS_USER_INFORMATION) === null) {
            this.getUserInformation().then(data => {
                if (typeof data === 'object' && Object.keys(data).length > 0) {
                    this.addLoginToStorage(data);
                    this.replaceUserInformation();
                } else {
                    this.displayLoggedOutInformation();
                }
                this.hidePlaceholderAndDisplayUserInformation();
                window.imaios.pubsub.publish(NOTIFICATIONS.USER_INFORMATIONS_AVAILABLE, TempStorageService.get(STORAGE_KEY.IMAIOS_USER_INFORMATION));
                imaios.store('are-credentials-reliable', true);
            }).catch(() => {
                this.hidePlaceholderAndDisplayUserInformation();
                this.displayLoggedOutInformation();
                imaios.store('are-credentials-reliable', false);
            });
        } else {
            this.hidePlaceholderAndDisplayUserInformation();
            this.replaceUserInformation();
            window.imaios.pubsub.publish(NOTIFICATIONS.USER_INFORMATIONS_AVAILABLE, TempStorageService.get(STORAGE_KEY.IMAIOS_USER_INFORMATION));
            imaios.store('are-credentials-reliable', true);
            void this.onConnectionCheckFinished();
        }
    }

    replaceUserInformation() {
        this.updateAccessViaThirdPartyCookie();
        const userName = this.getNameFromStorage();

        const identifier = this.getIdentifierFromStorage();
        if (identifier !== null) {
            const isLoginAs = this.getIsLoginAsFromStorage();
            const value = this.getValueFromStorage();
            this.replaceUserEmail(value, isLoginAs);
        } else {
            this.displayLoggedOutInformation();
        }

        if (userName !== null) {
            this.replaceUserName(userName);
        }

        let thirdPart = getCookie(COOKIE.THIRD_PARTY_PROVIDER);
        if (thirdPart) {
            try {
                thirdPart = JSON.parse(decodeURIComponent(thirdPart));
                if (thirdPart !== null && typeof thirdPart.provider !== 'undefined') {
                    this.replaceInstituteIfExist(thirdPart.provider, true);
                }
            } catch (err) {
                console.error(err);
            }
        } else {
            const data = this.getLocalUserData();
            const institute = this.getInstituteFromStorage();
            if (institute !== null) {
                this.replaceInstituteIfExist(institute, data.isIp ?? false);
            }
        }
    }

    replaceUserName(userName) {
        const userNameElement = document.getElementById('user-name-account');
        const userNameAvatarElement = document.getElementById('user-name-account-avatar');
        if (
            userNameElement
            && userNameAvatarElement
            && userName.firstName.length > 0
            && userName.lastName.length > 0
        ) {
            userNameElement.innerText = userName.firstName + ' ' + userName.lastName;
            userNameAvatarElement.innerHTML = `<p class="mb-0 text-xl text-center">${userName.firstName[0] + userName.lastName[0]}</p>`
        }
    }

    replaceUserEmail(value, isLoginAs) {

        const identifier = this.getIdentifierFromStorage();
        this.toggleDisplayLoginElements();

        if (value !== null) {
            const loggedOutElements = document.querySelectorAll('.imaios-login-logged-out');
            for (let i = 0; i < loggedOutElements.length; i++) {
                loggedOutElements[i].classList.add('d-none');
            }

            const loggedInElements = document.querySelectorAll('.imaios-login-logged-in');
            for (let i = 0; i < loggedInElements.length; i++) {
                loggedInElements[i].classList.remove('d-none');
            }

            const loginElements = document.querySelectorAll('.imaios-login');

            if (loginElements.length > 0) {
                const prefix = isLoginAs ? 'LOGGED IN AS ' : '';
                if (identifier === "AUTHENTICATION_EMAIL") {
                    for (let i = 0; i < loginElements.length; i++) {
                        if(loginElements[i].classList.contains('imaios-login-mobile')){
                            const emailSplit = value.split('');
                            let emailStr = emailSplit.slice(0, emailSplit.indexOf('@'));
                            loginElements[i].innerText = prefix + emailStr.join('');
                        }
                        else {
                            loginElements[i].innerText = prefix + value;
                        }
                    }
                } else if (identifier === "AUTHENTICATION_PHONE") {
                    for (let i = 0; i < loginElements.length; i++) {
                        loginElements[i].innerText = prefix + value;
                    }
                } else if (identifier === "AUTHENTICATION_WECHAT_UNION_ID") {
                    for (let i = 0; i < loginElements.length; i++) {
                        loginElements[i].innerText = this.getNameFromStorage().lastName;
                    }
                }
            }
        }
    }

    toggleDisplayLoginElements() {
        const loggedInElements = document.querySelectorAll('.imaios-login-logged-in');
        for (let i = 0; i < loggedInElements.length; i++) {
            loggedInElements[i].style.setProperty('display', '');
        }
    }

    /**
     * @return {LocalUserData|null}
     */
    getLocalUserData() {
        return TempStorageService.get(STORAGE_KEY.IMAIOS_USER_INFORMATION) ?? null;
    }

    getIdentifierFromStorage() {
        const data = this.getLocalUserData();
        if (data !== null) {
            return data.identifier;
        }
        return null;
    }

    getValueFromStorage() {
        const identifier = this.getIdentifierFromStorage();
        if (identifier !== null) {
            const data = this.getLocalUserData();
            return data.value;
        }
        return null;
    }

    getNameFromStorage() {
        const data = this.getLocalUserData();
        if (data !== null && data.firstName !== null && data.lastName !== null) {
            return {firstName: data.firstName, lastName: data.lastName};
        }
        return null;
    }

    getIsLoginAsFromStorage() {
        const data = this.getLocalUserData();
        if (data !== null) {
            return !!data.is_login_as;
        }
        return false;
    }

    getInstituteFromStorage() {
        const data = this.getLocalUserData();
        if (data !== null) {
            return data.b2b_customer_name;
        }
        return null;
    }

    addLoginToStorage(data) {
        TempStorageService.add(STORAGE_KEY.IMAIOS_USER_INFORMATION, data);
        void this.onConnectionCheckFinished();
    }

    unsubscribe() {
        this.subscriber.unsubscribeFromPublisherId(imaios.pubsub.getId());

        this.isInitialized = false;
    }

    subscribeToEvents() {
        if (!this.isInitialized) {
            this.isInitialized = true;
            this.subscriber.subscribe(imaios.pubsub, NOTIFICATIONS.LOGOUT, () => {
                TempStorageService.remove(STORAGE_KEY.IMAIOS_USER_INFORMATION);
                imaios.store('is-connected', false);
            });

            this.subscriber.subscribe(
                imaios.pubsub,
                NOTIFICATIONS.IS_REQUIRING_REFRESH_USER_INFO,
                () => {
                    TempStorageService.remove(STORAGE_KEY.IMAIOS_USER_INFORMATION);
                    this.loadLogin();
                }
            )

            const loggedOutElements = document.querySelectorAll('.imaios-login-logged-out a');
            for (let i = 0; i < loggedOutElements.length; i++) {
                loggedOutElements[i].addEventListener('click', (e) => {
                    imaios.pubsub.publish(NOTIFICATIONS.LOGOUT);
                });
            }

            imaios.pubsub.publish(NOTIFICATIONS.LOGIN_MANAGER_READY, this);
        }
    }

    checkExpire() {
        TempStorageService.checkExpire(STORAGE_KEY.IMAIOS_USER_INFORMATION);
    }

    replaceInstituteIfExist(institute, isInstitution = false) {
        if (institute !== null && isInstitution) {
            const instituteElements = document.querySelectorAll('.imaios-institute');

            for (let i = 0; i < instituteElements.length; i++) {
                instituteElements[i].style.setProperty('display', '');

                const instituteTextElement = instituteElements[i].querySelector('.imaios-institute-text');
                if (instituteTextElement !== null) {
                    instituteTextElement.innerText = institute;
                }
            }
        }
    }

    haveAccessToProduct(product) {
        const data = this.getLocalUserData();

        if (data !== null) {
            return typeof data.access[product] !== 'undefined';
        }
        return false;
    }

    deleteSubscriptionsTags() {
        if (this.haveAccessToProduct(PRODUCT.E_ANATOMY)) {
            this.deleteSubscriptionsTagsByApplication(PRODUCT.E_ANATOMY);
        }

        if (this.haveAccessToProduct(PRODUCT.VET_ANATOMY)) {
            this.deleteSubscriptionsTagsByApplication(PRODUCT.VET_ANATOMY);
        }
    }

    deleteSubscriptionsTagsByApplication(product) {
        const subscriptionTags = document.querySelectorAll(`.imaios-anatomy-subscription-tag-${product === PRODUCT.E_ANATOMY ? 'eanatomy' : 'vetanatomy'}`);

        for (let i = 0; i < subscriptionTags.length; i++) {
            subscriptionTags[i].style.setProperty('display', 'none', 'important');
        }
    }

    hidePlaceholderAndDisplayUserInformation() {
        imaios.store('is-connected', this.isConnected());
        const placeHolders = document.querySelectorAll('.imaios-placeholder-animated');
        for (let i = 0; i < placeHolders.length; i++) {
            placeHolders[i].style.setProperty('display', 'none', 'important');
        }

        const userInformationElements = document.querySelectorAll('.user-information');
        for (let i = 0; i < userInformationElements.length; i++) {
            userInformationElements[i].style.setProperty('display', '');
        }
    }

    displayLoggedOutInformation() {
        const loggedOutElements = document.querySelectorAll('.imaios-login-logged-out');
        for (let i = 0; i < loggedOutElements.length; i++) {
            loggedOutElements[i].style.setProperty('display', '');
            loggedOutElements[i].classList.remove('d-none');
        }

        const loggedInElements = document.querySelectorAll('.imaios-login-logged-in');
        for (let i = 0; i < loggedInElements.length; i++) {
            loggedInElements[i].classList.add('d-none');
        }
    }

    /**
     * @returns {Promise<{}|UserProductAccess>}
     */
    async getUserAccess() {
        this.checkExpire();

        if (TempStorageService.get(STORAGE_KEY.IMAIOS_USER_INFORMATION) === null) {
            try {
                const data = await this.getUserInformation();
                if (typeof data === 'object' && Object.keys(data).length > 0) {
                    this.addLoginToStorage(data);
                }
            } catch (e) {
                // Return empty data
                return {};
            }
        }

        const data = this.getLocalUserData();
        if (data !== null) {
            return data.access;
        }
        return {};
    }

    updateAccessViaThirdPartyCookie() {
        let thirdPartyCookie = getCookie(COOKIE.THIRD_PARTY_PROVIDER);
        if (thirdPartyCookie) {
            thirdPartyCookie = JSON.parse(decodeURIComponent(thirdPartyCookie));

            const data = this.getLocalUserData();
            if (data !== null) {
                data.access[THIRD_PARTY_PROVIDER_PRODUCT[thirdPartyCookie.product.toUpperCase()]] = {
                    group_premium: true
                };

                this.addLoginToStorage(data);
            }
        }
    }

    /**
     * Returns a promise that is resolved once the connection information has been validated.
     * @return {Promise<void>}
     */
    async awaitConnection() {
        if(imaios.get('are-credentials-reliable')) {
            return;
        }

        return await this.awaitableConnection;
    }
}

export default new LoginManagementService();
